import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Pencil, Plus, Megaphone, Pin, PinOff, Trash2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  AdvancedListToolbar,
  useUrlSearchState,
  type FilterChip
} from '@/components/ui/advanced-list-toolbar';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime } from '@/lib/format';
import type { Notice, NoticeCategory } from '@hooddesk/shared-types';

const ComposeSchema = z
  .object({
    title: z.string().min(2, 'Title required').max(200),
    body: z.string().min(2, 'Body required').max(8000),
    category: z.enum(['general', 'maintenance', 'security', 'event', 'billing']),
    audience: z.enum(['all', 'owners', 'tenants', 'block', 'unit']),
    audience_block_id: z.string().uuid().optional().or(z.literal('')),
    audience_unit_id: z.string().uuid().optional().or(z.literal('')),
    publish_at: z.string().optional()
  })
  .superRefine((v, ctx) => {
    if (v.audience === 'block' && !v.audience_block_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['audience_block_id'],
        message: 'Pick a block when audience = Block'
      });
    }
    if (v.audience === 'unit' && (!v.audience_block_id || !v.audience_unit_id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['audience_unit_id'],
        message: 'Pick a block and a unit when audience = Specific unit'
      });
    }
  });

type ComposeForm = z.infer<typeof ComposeSchema>;

interface NoticeListResponse {
  items: Notice[];
  next_cursor?: string | null;
}

const CATEGORY_VARIANT: Record<NoticeCategory, 'info' | 'warn' | 'alert' | 'ok' | 'muted'> = {
  general: 'info',
  maintenance: 'warn',
  security: 'alert',
  event: 'ok',
  billing: 'muted'
};

interface DeliveryStats {
  notice_id: string;
  status: string;
  published_at: string | null;
  audience: string;
  audience_size: number;
  reachable_devices: number;
  dispatched: number;
  sent: number;
  failed: number;
  device_not_registered: number;
  queued: number;
  delivered_estimate: number;
  read_count: number | null;
}

/** True once every dispatched push has reached a terminal state (sent / failed / unregistered). */
function isDeliveryTerminal(d: DeliveryStats | undefined): boolean {
  if (!d) return false;
  return d.queued === 0 && d.sent + d.failed + d.device_not_registered >= d.dispatched;
}

// URL-synced filter+sort+page state. NOTE on schema mismatch with the
// Phase 2B spec:
//   - `severity` was requested but the schema has no severity column; the
//     existing `category` enum already encodes it. We re-use category here.
//   - `status=scheduled` is not in the NoticeStatus enum (draft/published/
//     expired). Scheduled rows have status='draft' with publish_at in future.
//   - `scope` is mapped onto the `audience` column. The audience enum has no
//     `tower` member — clients should pick `block` for tower-level scoping.
const NOTICE_FILTER_DEFAULTS = {
  search: '',
  category: '',
  scope: '', // maps to backend `scope` → audience column
  status: '',
  sort: 'created_desc',
  page_size: 50
};

const CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'general', label: 'General' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'security', label: 'Security' },
  { value: 'event', label: 'Event' },
  { value: 'billing', label: 'Billing' }
];

const SCOPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'society', label: 'Society' },
  { value: 'block', label: 'Block' },
  { value: 'unit', label: 'Unit' }
];

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'expired', label: 'Expired' }
];

export function NoticesPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Notice | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [lastSentNoticeId, setLastSentNoticeId] = useState<string | null>(null);
  const [deliveryDetailsOpen, setDeliveryDetailsOpen] = useState(false);
  const [filterState, setFilterState] = useUrlSearchState(NOTICE_FILTER_DEFAULTS);
  const [searchInput, setSearchInput] = useState<string>(filterState.search);
  const queryClient = useQueryClient();

  // Debounce free-text search → URL state.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (searchInput !== filterState.search) {
        setFilterState({ search: searchInput });
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const filtersHash = useMemo(
    () =>
      JSON.stringify([
        filterState.search,
        filterState.category,
        filterState.scope,
        filterState.status,
        filterState.sort,
        filterState.page_size
      ]),
    [filterState]
  );

  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      limit: filterState.page_size,
      sort: filterState.sort
    };
    if (filterState.search) params.q = filterState.search;
    if (filterState.category) params.category = filterState.category;
    if (filterState.scope) params.scope = filterState.scope;
    if (filterState.status) params.status = filterState.status;
    return params;
  }, [filterState]);

  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);
  const list = useQuery({
    queryKey: ['notices', queryParams, cursor],
    queryFn: () =>
      get<NoticeListResponse>('/notices', {
        params: { ...queryParams, ...(cursor ? { cursor } : {}) }
      }),
    placeholderData: (prev) => prev
  });
  const paginator = useCursorAccumulator<Notice>(list.data, cursor, filtersHash);

  const editingNotice = useMemo(
    () => (editingId ? paginator.items.find((n) => n.id === editingId) ?? null : null),
    [editingId, paginator.items]
  );

  // After a publish, poll delivery stats every 5s for ~30s so the admin sees the count grow.
  // Stop early once every dispatched push has reached a terminal state.
  const delivery = useQuery({
    queryKey: ['notice-delivery', lastSentNoticeId],
    queryFn: () =>
      lastSentNoticeId
        ? get<DeliveryStats>(`/notices/${lastSentNoticeId}/delivery`)
        : Promise.reject(new Error('no notice')),
    enabled: Boolean(lastSentNoticeId),
    refetchInterval: (query) => {
      if (!lastSentNoticeId) return false;
      const data = query.state.data as DeliveryStats | undefined;
      return isDeliveryTerminal(data) ? false : 5_000;
    },
    staleTime: 0
  });

  const form = useForm<ComposeForm>({
    resolver: zodResolver(ComposeSchema),
    defaultValues: {
      category: 'general',
      audience: 'all',
      title: '',
      body: '',
      audience_block_id: '',
      audience_unit_id: ''
    }
  });

  const audienceValue = form.watch('audience');
  const blockIdValue = form.watch('audience_block_id') ?? '';

  // Blocks list — always loaded; cheap (few rows per society).
  const blocksList = useQuery({
    queryKey: ['blocks-for-notice'],
    queryFn: () =>
      get<{ items: Array<{ id: string; name: string; _count?: { units: number } }> }>(
        '/society/blocks',
        { params: { limit: 100 } }
      ),
    staleTime: 60_000
  });

  // Units list — lazily fetched only when audience='unit' AND a block is chosen.
  const unitsList = useQuery({
    queryKey: ['units-for-notice', blockIdValue],
    queryFn: () =>
      get<{ items: Array<{ id: string; number: string }> }>('/society/units', {
        params: { block_id: blockIdValue, limit: 200 }
      }),
    enabled: audienceValue === 'unit' && Boolean(blockIdValue),
    staleTime: 60_000
  });

  const create = useMutation({
    mutationFn: (values: ComposeForm) => post<Notice, ComposeForm>('/notices', values),
    onSuccess: (notice) => {
      toast.success(`Notice "${notice.title}" published`);
      void queryClient.invalidateQueries({ queryKey: ['notices'] });
      setLastSentNoticeId(notice.id);
      // Stop polling after 30s — by then audience-size proxy is stable
      window.setTimeout(() => setLastSentNoticeId((curr) => (curr === notice.id ? null : curr)), 30_000);
      setOpen(false);
      form.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const update = useMutation({
    mutationFn: ({ id, values }: { id: string; values: ComposeForm }) =>
      patch<Notice>(`/notices/${id}`, values),
    onSuccess: (notice) => {
      toast.success(`Notice "${notice.title}" updated`);
      void queryClient.invalidateQueries({ queryKey: ['notices'] });
      setEditingId(null);
      setOpen(false);
      form.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  useEffect(() => {
    if (editingNotice && open) {
      const en = editingNotice as Notice & {
        audience_block_id?: string | null;
        audience_unit_id?: string | null;
      };
      form.reset({
        title: editingNotice.title,
        body: editingNotice.body,
        category: editingNotice.category,
        audience: editingNotice.audience as ComposeForm['audience'],
        audience_block_id: en.audience_block_id ?? '',
        audience_unit_id: en.audience_unit_id ?? ''
      });
    } else if (!open && !editingId) {
      form.reset({
        category: 'general',
        audience: 'all',
        title: '',
        body: '',
        audience_block_id: '',
        audience_unit_id: ''
      });
    }
  }, [editingNotice, open, editingId, form]);

  const onSubmit = (v: ComposeForm) => {
    if (editingId) update.mutate({ id: editingId, values: v });
    else create.mutate(v);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingId(null);
  };

  const togglePin = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      patch<Notice>(`/notices/${id}`, { pinned }),
    onSuccess: (n) => {
      toast.success(n.pinned ? 'Pinned to top' : 'Unpinned');
      void queryClient.invalidateQueries({ queryKey: ['notices'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<void>(`/notices/${id}`),
    onSuccess: () => {
      toast.success('Notice deleted');
      void queryClient.invalidateQueries({ queryKey: ['notices'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Sort + filter chip wiring for the shared toolbar.
  const sortOptions = useMemo(
    () => [
      { value: 'created_desc', label: 'Created (newest)' },
      { value: 'published_at_desc', label: 'Published (newest)' },
      { value: 'title_asc', label: 'Title (A–Z)' }
    ],
    []
  );

  const filterChips: FilterChip[] = useMemo(
    () => [
      {
        id: 'category',
        label: 'Category',
        type: 'select',
        value: filterState.category,
        options: CATEGORY_OPTIONS,
        onChange: (v) => setFilterState({ category: v })
      },
      {
        id: 'scope',
        label: 'Scope',
        type: 'select',
        value: filterState.scope,
        options: SCOPE_OPTIONS,
        onChange: (v) => setFilterState({ scope: v })
      },
      {
        id: 'status',
        label: 'Status',
        type: 'select',
        value: filterState.status,
        options: STATUS_OPTIONS,
        onChange: (v) => setFilterState({ status: v })
      }
    ],
    [filterState, setFilterState]
  );

  const hasActiveFilters =
    filterState.search !== NOTICE_FILTER_DEFAULTS.search ||
    filterState.category !== NOTICE_FILTER_DEFAULTS.category ||
    filterState.scope !== NOTICE_FILTER_DEFAULTS.scope ||
    filterState.status !== NOTICE_FILTER_DEFAULTS.status ||
    filterState.sort !== NOTICE_FILTER_DEFAULTS.sort ||
    filterState.page_size !== NOTICE_FILTER_DEFAULTS.page_size;

  const resetFilters = () => {
    setFilterState(NOTICE_FILTER_DEFAULTS);
    setSearchInput('');
  };

  return (
    <>
      <PageHeader
        title="Notices"
        description="Publish announcements, maintenance alerts, and event invites to residents."
        actions={
          <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={() => setEditingId(null)}>
                <Plus className="h-4 w-4" /> Compose notice
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? 'Edit notice' : 'Compose notice'}</DialogTitle>
                <DialogDescription>
                  {editingId
                    ? 'Update title, body, category, or audience. Changes propagate to all residents instantly.'
                    : 'Sent to all targeted residents in the society. They receive an in-app notification and email.'}
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col gap-vt-4"
              >
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    placeholder="Water tank cleaning on 12 May, 10am–1pm"
                    aria-invalid={Boolean(form.formState.errors.title) || undefined}
                    {...form.register('title')}
                  />
                  {form.formState.errors.title ? (
                    <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.title.message}</p>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-vt-3">
                  <div className="flex flex-col gap-vt-1">
                    <Label htmlFor="category">Category</Label>
                    <Select id="category" {...form.register('category')}>
                      <option value="general">General</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="security">Security</option>
                      <option value="event">Event</option>
                      <option value="billing">Billing</option>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-vt-1">
                    <Label htmlFor="audience">Audience</Label>
                    <Select
                      id="audience"
                      {...form.register('audience', {
                        onChange: () => {
                          // Reset cascading IDs when audience scope changes
                          form.setValue('audience_block_id', '');
                          form.setValue('audience_unit_id', '');
                        }
                      })}
                    >
                      <option value="all">All residents</option>
                      <option value="owners">Owners only</option>
                      <option value="tenants">Tenants only</option>
                      <option value="block">Block</option>
                      <option value="unit">Specific unit</option>
                    </Select>
                  </div>
                </div>

                {/* Cascading scope pickers — only visible for Block / Unit audiences */}
                {audienceValue === 'block' || audienceValue === 'unit' ? (
                  <div className="grid grid-cols-2 gap-vt-3 rounded-vt-md border border-dashed border-vt-border bg-vt-surface-2/40 p-vt-3">
                    <div className="flex flex-col gap-vt-1">
                      <Label htmlFor="audience_block_id">Block</Label>
                      <Select
                        id="audience_block_id"
                        {...form.register('audience_block_id', {
                          onChange: () => form.setValue('audience_unit_id', '')
                        })}
                      >
                        <option value="">— pick a block —</option>
                        {blocksList.data?.items.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                            {b._count?.units ? ` (${b._count.units} units)` : ''}
                          </option>
                        ))}
                      </Select>
                      {form.formState.errors.audience_block_id ? (
                        <p className="text-vt-12 text-vt-alert" role="alert">
                          {form.formState.errors.audience_block_id.message}
                        </p>
                      ) : null}
                    </div>
                    {audienceValue === 'unit' ? (
                      <div className="flex flex-col gap-vt-1">
                        <Label htmlFor="audience_unit_id">Unit</Label>
                        <Select
                          id="audience_unit_id"
                          disabled={!blockIdValue || unitsList.isLoading}
                          {...form.register('audience_unit_id')}
                        >
                          <option value="">
                            {!blockIdValue ? '— pick block first —' : unitsList.isLoading ? 'Loading…' : '— pick a unit —'}
                          </option>
                          {unitsList.data?.items.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.number}
                            </option>
                          ))}
                        </Select>
                        {form.formState.errors.audience_unit_id ? (
                          <p className="text-vt-12 text-vt-alert" role="alert">
                            {form.formState.errors.audience_unit_id.message}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="body">Message</Label>
                  <Textarea
                    id="body"
                    rows={6}
                    placeholder="Provide context, time window, and any action residents need to take."
                    aria-invalid={Boolean(form.formState.errors.body) || undefined}
                    {...form.register('body')}
                  />
                  {form.formState.errors.body ? (
                    <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.body.message}</p>
                  ) : (
                    <p className="text-vt-12 text-vt-text-soft">Markdown is rendered in the resident app.</p>
                  )}
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={closeDialog}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={create.isPending || update.isPending}
                    loadingText={editingId ? 'Saving…' : 'Publishing…'}
                  >
                    {editingId ? 'Save changes' : 'Publish'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-3 px-vt-6 pb-vt-8 pt-vt-4">
        <AdvancedListToolbar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search title or body…"
          filters={filterChips}
          sort={{
            value: filterState.sort,
            onChange: (v) => setFilterState({ sort: v }),
            options: sortOptions
          }}
          pageSize={filterState.page_size}
          onPageSizeChange={(n) => setFilterState({ page_size: n })}
          onReset={resetFilters}
          hasActiveFilters={hasActiveFilters}
          resultLabel={
            list.data
              ? `${paginator.items.length} result${paginator.items.length === 1 ? '' : 's'}`
              : 'Loading…'
          }
        />

        {/* Live delivery banner — shown for ~30s after a publish */}
        {lastSentNoticeId && delivery.data ? (
          <div className="rounded-vt-md border border-vt-info/25 bg-[color:var(--vt-info-100)] px-vt-4 py-vt-3 text-vt-13">
            <div className="flex flex-wrap items-center justify-between gap-vt-3">
              <div className="flex flex-wrap items-center gap-vt-2 text-[color:var(--vt-info)]">
                <Send className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span className="font-medium">Delivered:</span>
                <Badge variant="ok">{delivery.data.sent} sent</Badge>
                <Badge variant="alert">{delivery.data.failed} failed</Badge>
                <Badge variant="warn">{delivery.data.queued} queued</Badge>
                <Badge variant="muted">{delivery.data.device_not_registered} unregistered</Badge>
                <Badge variant="info">{delivery.data.read_count ?? 0} read</Badge>
                <span className="text-vt-12 text-[color:var(--vt-info)]/80">
                  of <strong>{delivery.data.reachable_devices}</strong> reachable /{' '}
                  <strong>{delivery.data.audience_size}</strong> audience
                </span>
                {delivery.isFetching && !isDeliveryTerminal(delivery.data) ? (
                  <span className="text-vt-12 opacity-70">· refreshing…</span>
                ) : null}
              </div>
              <button
                type="button"
                className="text-vt-12 text-[color:var(--vt-info)] underline-offset-2 hover:underline"
                onClick={() => setLastSentNoticeId(null)}
              >
                Dismiss
              </button>
            </div>
            <div className="mt-vt-2">
              <button
                type="button"
                className="text-vt-12 text-[color:var(--vt-info)] underline underline-offset-2 hover:opacity-80"
                onClick={() => setDeliveryDetailsOpen(true)}
              >
                View delivery details →
              </button>
            </div>
          </div>
        ) : null}

        {list.isLoading ? (
          <div className="grid gap-vt-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<Megaphone className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load notices"
                description={(list.error as Error)?.message}
                action={
                  <Button variant="secondary" onClick={() => void list.refetch()}>
                    Try again
                  </Button>
                }
              />
            </CardContent>
          </Card>
        ) : paginator.items.length === 0 ? (
          <Card>
            <CardContent>
              {hasActiveFilters ? (
                <EmptyState
                  icon={<Megaphone className="h-6 w-6" strokeWidth={1.75} />}
                  title="No notices match your filters"
                  description="Try widening the filters or clear them to see every notice on file."
                  action={
                    <Button variant="secondary" onClick={resetFilters}>
                      Reset filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<Megaphone className="h-6 w-6" strokeWidth={1.75} />}
                  title="No notices yet"
                  description="Compose your first notice to inform residents about events, maintenance, or alerts."
                  action={
                    <Button onClick={() => setOpen(true)}>
                      <Plus className="h-4 w-4" /> Compose notice
                    </Button>
                  }
                />
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-vt-3">
            {paginator.items.map((n) => (
              <Card key={n.id} interactive className="overflow-hidden">
                <CardHeader className="pb-vt-2">
                  <div className="flex items-start justify-between gap-vt-3">
                    <div className="min-w-0">
                      <CardTitle className="flex items-center gap-vt-2 text-vt-16">
                        {n.pinned ? (
                          <Pin className="h-4 w-4 text-vt-warn" aria-label="Pinned" strokeWidth={2} />
                        ) : null}
                        <span className="truncate">{n.title}</span>
                      </CardTitle>
                      <CardDescription className="mt-vt-1 capitalize">
                        {n.audience.replace('_', ' ')} · {formatDateTime(n.published_at ?? n.publish_at)}
                      </CardDescription>
                    </div>
                    <div className="flex shrink-0 items-center gap-vt-1">
                      <Badge variant={CATEGORY_VARIANT[n.category]} className="capitalize">
                        {n.category}
                      </Badge>
                      <Badge variant={n.status === 'published' ? 'ok' : 'muted'} className="capitalize">
                        {n.status}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title={n.pinned ? 'Unpin' : 'Pin to top'}
                        disabled={togglePin.isPending}
                        onClick={() => togglePin.mutate({ id: n.id, pinned: !n.pinned })}
                      >
                        {n.pinned ? (
                          <PinOff className="h-4 w-4 text-vt-warn" />
                        ) : (
                          <Pin className="h-4 w-4 text-vt-text-soft" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-vt-text-soft"
                        title="Edit notice"
                        onClick={() => {
                          setEditingId(n.id);
                          setOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-vt-text-soft"
                        title="Delete notice"
                        disabled={remove.isPending}
                        onClick={() => setRemoveTarget(n)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-line text-vt-14 leading-relaxed text-vt-text">{n.body}</p>
                </CardContent>
              </Card>
            ))}
            <CursorPaginator
              hasMore={Boolean(paginator.nextCursor)}
              loading={list.isFetching}
              itemsShown={paginator.items.length}
              pageSize={filterState.page_size}
              onLoadMore={() => {
                if (paginator.nextCursor) setCursor(paginator.nextCursor);
              }}
            />
          </div>
        )}
      </div>

      {/* Verbose delivery breakdown dialog */}
      <Dialog
        open={deliveryDetailsOpen}
        onOpenChange={(v) => setDeliveryDetailsOpen(v)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delivery details</DialogTitle>
            <DialogDescription>
              Per-status breakdown for the most recently published notice. Per-recipient list is
              not yet available.
            </DialogDescription>
          </DialogHeader>
          {delivery.data ? (
            <div className="space-y-vt-3 py-vt-2">
              <div className="grid grid-cols-2 gap-vt-3 text-vt-13">
                <div>
                  <div className="text-vt-12 text-vt-text-muted">Audience</div>
                  <div className="text-vt-text capitalize">{delivery.data.audience}</div>
                </div>
                <div>
                  <div className="text-vt-12 text-vt-text-muted">Status</div>
                  <div className="text-vt-text capitalize">{delivery.data.status}</div>
                </div>
                <div>
                  <div className="text-vt-12 text-vt-text-muted">Audience size</div>
                  <div className="font-semibold text-vt-text">{delivery.data.audience_size}</div>
                </div>
                <div>
                  <div className="text-vt-12 text-vt-text-muted">Reachable devices</div>
                  <div className="font-semibold text-vt-text">{delivery.data.reachable_devices}</div>
                </div>
                <div>
                  <div className="text-vt-12 text-vt-text-muted">Dispatched</div>
                  <div className="font-semibold text-vt-text">{delivery.data.dispatched}</div>
                </div>
                <div>
                  <div className="text-vt-12 text-vt-text-muted">Delivered (est.)</div>
                  <div className="font-semibold text-vt-text">{delivery.data.delivered_estimate}</div>
                </div>
              </div>

              <div className="border-t border-vt-border pt-vt-3">
                <div className="mb-vt-2 text-vt-12 font-medium uppercase tracking-wide text-vt-text-muted">
                  Per-status counts
                </div>
                <div className="flex flex-col gap-vt-2 text-vt-13">
                  <div className="flex items-center justify-between">
                    <Badge variant="ok">sent</Badge>
                    <span className="font-semibold text-vt-text">{delivery.data.sent}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge variant="alert">failed</Badge>
                    <span className="font-semibold text-vt-text">{delivery.data.failed}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge variant="warn">queued</Badge>
                    <span className="font-semibold text-vt-text">{delivery.data.queued}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge variant="muted">device not registered</Badge>
                    <span className="font-semibold text-vt-text">
                      {delivery.data.device_not_registered}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge variant="info">read</Badge>
                    <span className="font-semibold text-vt-text">
                      {delivery.data.read_count ?? 0}
                    </span>
                  </div>
                </div>
              </div>

              {delivery.data.published_at ? (
                <div className="border-t border-vt-border pt-vt-3 text-vt-12 text-vt-text-muted">
                  Published {formatDateTime(delivery.data.published_at)}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="py-vt-4 text-vt-13 text-vt-text-muted">No delivery data available.</div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDeliveryDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onOpenChange={(v) => (v ? null : setRemoveTarget(null))}
        title="Delete notice"
        description={
          removeTarget ? (
            <>
              Delete notice <strong>{removeTarget.title}</strong>? Residents lose access to the
              announcement immediately.
            </>
          ) : null
        }
        confirmLabel="Delete notice"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (!removeTarget) return;
          remove.mutate(removeTarget.id, { onSuccess: () => setRemoveTarget(null) });
        }}
      />
    </>
  );
}
